import React, { useState, useEffect } from 'react';
import { Calculator, DollarSign, Calendar, TrendingUp, RefreshCw } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { Newsletter } from '../components/Newsletter';
import { FinancialChart } from '../components/Chart';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

interface InvestmentData {
  year: number;
  startBalance: number;
  contribution: number;
  earnings: number;
  endBalance: number;
}

const InvestmentCalculator = () => {
  const [initialAmount, setInitialAmount] = useState<number>(10000);
  const [regularContribution, setRegularContribution] = useState<number>(500);
  const [timeframe, setTimeframe] = useState<number>(10);
  const [returnRate, setReturnRate] = useState<number>(8);
  const [compoundFrequency, setCompoundFrequency] = useState<'monthly' | 'quarterly' | 'annually'>('monthly');
  const [projections, setProjections] = useState<InvestmentData[]>([]);

  const calculateInvestment = () => {
    const periodsPerYear = compoundFrequency === 'monthly' ? 12 : compoundFrequency === 'quarterly' ? 4 : 1;
    const periodicRate = (returnRate / 100) / periodsPerYear;
    const totalPeriods = timeframe * periodsPerYear;
    
    let balance = initialAmount;
    let projectionData: InvestmentData[] = [];
    
    for (let year = 1; year <= timeframe; year++) {
      const startBalance = balance;
      const yearlyContribution = regularContribution * periodsPerYear;
      
      for (let period = 1; period <= periodsPerYear; period++) {
        const periodContribution = regularContribution;
        balance = (balance + periodContribution) * (1 + periodicRate);
      }
      
      const earnings = balance - startBalance - yearlyContribution;
      
      projectionData.push({
        year,
        startBalance,
        contribution: yearlyContribution,
        earnings,
        endBalance: balance
      });
    }
    
    setProjections(projectionData);
  };

  useEffect(() => {
    calculateInvestment();
  }, [initialAmount, regularContribution, timeframe, returnRate, compoundFrequency]);

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    doc.setFontSize(20);
    doc.text('Investment Growth Projection', 14, 22);
    
    doc.setFontSize(12);
    doc.text(`Initial Investment: $${initialAmount.toLocaleString()}`, 14, 32);
    doc.text(`Regular Contribution: $${regularContribution.toLocaleString()}`, 14, 40);
    doc.text(`Investment Period: ${timeframe} years`, 14, 48);
    doc.text(`Expected Return: ${returnRate}%`, 14, 56);
    doc.text(`Compound Frequency: ${compoundFrequency}`, 14, 64);
    
    const tableData = projections.map(row => [
      row.year,
      `$${row.startBalance.toLocaleString()}`,
      `$${row.contribution.toLocaleString()}`,
      `$${row.earnings.toLocaleString()}`,
      `$${row.endBalance.toLocaleString()}`
    ]);
    
    (doc as any).autoTable({
      head: [['Year', 'Start Balance', 'Contribution', 'Earnings', 'End Balance']],
      body: tableData,
      startY: 72,
      theme: 'grid',
      styles: { fontSize: 8 }
    });
    
    doc.save('investment-projection.pdf');
  };

  const chartData = {
    labels: projections.map(p => `Year ${p.year}`),
    datasets: [
      {
        label: 'Total Investment Value',
        data: projections.map(p => p.endBalance),
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Total Contributions',
        data: projections.map(p => p.startBalance + p.contribution),
        borderColor: '#34d399',
        backgroundColor: 'rgba(52, 211, 153, 0.1)',
        fill: true,
        tension: 0.4
      }
    ]
  };

  return (
    <div className="min-h-screen bg-[var(--background)] p-6">
      <div className="max-w-7xl mx-auto">
        <Navigation />

        <div className="mb-8">
          <Newsletter 
            title="Grow Your Investment Knowledge"
            subtitle="Subscribe for investment strategies, market insights, and portfolio optimization tips."
          />
        </div>
        
        <div className="glass-card p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-xl bg-emerald-500/10">
              <Calculator className="w-6 h-6 text-emerald-500" />
            </div>
            <h2 className="text-2xl font-semibold">Investment Calculator</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <div className="space-y-2">
              <label className="block text-sm text-slate-400">Initial Investment</label>
              <div className="relative">
                <DollarSign className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="number"
                  value={initialAmount}
                  onChange={(e) => setInitialAmount(Number(e.target.value))}
                  className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                  min="0"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm text-slate-400">Regular Contribution</label>
              <div className="relative">
                <DollarSign className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="number"
                  value={regularContribution}
                  onChange={(e) => setRegularContribution(Number(e.target.value))}
                  className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                  min="0"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm text-slate-400">Investment Period (Years)</label>
              <div className="relative">
                <Calendar className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="number"
                  value={timeframe}
                  onChange={(e) => setTimeframe(Number(e.target.value))}
                  className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                  min="1"
                  max="50"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm text-slate-400">Expected Return Rate (%)</label>
              <div className="relative">
                <TrendingUp className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="number"
                  value={returnRate}
                  onChange={(e) => setReturnRate(Number(e.target.value))}
                  className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                  min="0"
                  max="30"
                  step="0.1"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm text-slate-400">Compound Frequency</label>
              <div className="relative">
                <RefreshCw className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <select
                  value={compoundFrequency}
                  onChange={(e) => setCompoundFrequency(e.target.value as 'monthly' | 'quarterly' | 'annually')}
                  className="glass-input pl-10 pr-4 py-2 w-full rounded-lg appearance-none"
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annually">Annually</option>
                </select>
              </div>
            </div>
          </div>

          <button
            onClick={exportToPDF}
            className="px-4 py-2 bg-emerald-500/10 text-emerald-500 rounded-lg hover:bg-emerald-500/20 transition-colors"
          >
            Export to PDF
          </button>
        </div>

        <div className="glass-card p-8 mb-8">
          <h3 className="text-xl font-semibold mb-6">Investment Growth Projection</h3>
          <FinancialChart
            type="line"
            data={chartData}
            height={400}
            className="mb-8"
          />
        </div>

        <div className="glass-card p-8">
          <h3 className="text-xl font-semibold mb-6">Investment Projections</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left p-3">Year</th>
                  <th className="text-right p-3">Start Balance</th>
                  <th className="text-right p-3">Contribution</th>
                  <th className="text-right p-3">Earnings</th>
                  <th className="text-right p-3">End Balance</th>
                </tr>
              </thead>
              <tbody>
                {projections.map((row, index) => (
                  <tr key={index} className="border-b border-slate-800 hover:bg-slate-800/30">
                    <td className="p-3">{row.year}</td>
                    <td className="text-right p-3">${row.startBalance.toLocaleString()}</td>
                    <td className="text-right p-3">${row.contribution.toLocaleString()}</td>
                    <td className="text-right p-3">${row.earnings.toLocaleString()}</td>
                    <td className="text-right p-3">${row.endBalance.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-8">
          <Newsletter 
            title="Maximize Your Returns"
            subtitle="Get expert investment advice and market analysis delivered to your inbox."
          />
        </div>
      </div>
    </div>
  );
};

export default InvestmentCalculator;